import React from "react";

export function EnglishTaC() {
    return <div className="container terms-and-conditions">
        <h1>Terms & Conditions</h1>
        <p><strong>Last updated:</strong> 10 October 2025</p>

        <p>Please read these Terms & Conditions (“<strong>Terms</strong>”) carefully before using the Sælv application
            (the “<strong>Service</strong>”), operated by <strong>Sælv (sole proprietorship)</strong>, Otto Brandenburgs
            Gade 16, 8000 Aarhus C, Denmark (“<strong>Sælv</strong>”, “we”, “us”, or “our”). By using the Service, you
            agree to these Terms. If you do not agree, you must not use the Service.</p>

        <h2>1. Provider (service provider details)</h2>
        <ul>
            <li>Sælv (sole proprietorship)</li>
            <li>CVR: 45234576</li>
            <li>Address: Otto Brandenburgs Gade 16, 8000 Aarhus C, Denmark</li>
            <li>E-mail (users): <a href="mailto:kontakt@saelv.dk">kontakt@saelv.dk</a></li>
            <li>DSA single point of contact (users/authorities): <a
                href="mailto:kontakt@saelv.dk">kontakt@saelv.dk</a> (Danish/English)
            </li>
        </ul>

        <h2>2. Nature and scope of the Service</h2>
        <p>The Service enables users to <strong>add and discover farm shops, roadside stands, honesty boxes/shelves, and
            other reuse/homegrown locations</strong> on a map. Content is generally visible to the public. The Service
            can be used in the <strong>Denmark</strong>.</p>

        <h2>3. Account and age</h2>
        <p>You may use the Service without an account. If you create an account, you must provide accurate and
            up‑to‑date information (e.g., name and e‑mail). Do not share your password. Notify us immediately of
            suspected misuse. We may suspend or close accounts for breaches of these Terms or security risks.</p>

        <h2>4. Licence</h2>
        <p>We grant you a personal, non‑exclusive, non‑transferable, revocable licence to install and use the app in
            accordance with these Terms.</p>

        <h2>5. User‑generated content (UGC)</h2>
        <ul>
            <li><strong>Ownership:</strong> You retain rights to the text and map content you submit to the Service.
            </li>
            <li><strong>Licence to us:</strong> You grant us a worldwide, non‑exclusive, royalty‑free licence (with the
                right to sublicense to operations/hosting partners) to store, host, copy, transmit, and display content
                solely to provide the Service. The licence ends when the content or your account is deleted, except to
                the extent we must temporarily retain log or backup copies for security/operations or for legally
                required periods.
            </li>
            <li><strong>Warranties:</strong> You warrant that you hold necessary rights and that your content is not
                illegal, infringing, misleading, harmful, defamatory, discriminatory, or containing unauthorised
                personal data/malware.
            </li>
            <li><strong>Moderation (manual):</strong> To protect quality, we <strong>manually approve</strong> all new
                places (stands, etc.) and changes to crowd‑sourced items without an identifiable owner. We may remove or
                disable content on suspected breaches of law or these Terms.
            </li>
            <li><strong>Reporting (notice &amp; action):</strong> Content can be <strong>reported directly
                in‑app</strong> from the relevant item. Please include a description and contact details if you are not logged in. We assess and act without undue delay and will notify you of our decision.
            </li>
        </ul>

        <h2>6. DSA disclosures</h2>
        <p><strong>6.1. Public dissemination:</strong> Because content is made available to the public at users’
            request, Sælv operates as a hosting service/online platform. We apply the moderation approach described in
            Sections 5 and 23.</p>
        <p><strong>6.2. Statement of reasons:</strong> If we remove or restrict content/access, we provide a brief,
            reasoned decision to the affected user (and the owner, where relevant), including information on available
            complaints.</p>
        <p><strong>6.3. Internal complaint handling:</strong> You may <strong>appeal free of
            charge</strong> within <strong>6 months</strong> of our decision via the app or <a
            href="mailto:kontakt@saelv.dk">kontakt@saelv.dk</a>. Complaints are handled promptly under qualified staff
            oversight. Independent dispute settlement may be used where applicable.</p>
        <p><strong>6.4. Contact point:</strong> Our single point of contact for users and authorities is <a
            href="mailto:kontakt@saelv.dk">kontakt@saelv.dk</a> (Danish/English).</p>
        <p><strong>6.5. Ranking/recommendation parameters:</strong> What you see is primarily determined by <strong>distance
            from your location</strong> (radius filtering). You can influence results by adjusting your device’s
            location permissions/settings and the radius used by the Service. No targeted advertising is used.</p>

        <h2>7. Prohibited conduct</h2>
        <p>Prohibited: bypassing security, reverse engineering (except where non‑waivable by law), automated scraping,
            uploading malware, harassment, hateful/discriminatory content, infringement of third‑party rights, and
            attempts at unauthorised access.</p>

        <h2>8. Donations (In‑App Purchases)</h2>
        <p>You may make voluntary <strong>one‑off donations</strong> to support operations. Donations are processed in
            DKK or in <strong>the currencies supported by Apple</strong>. The amount and any applicable VAT are shown in
            the purchase flow. <strong>Payments, receipts, and consumer payment enquiries are handled by Apple</strong>.
            Sælv offers <strong>no refunds</strong> unless required by mandatory law.</p>
        <p>Donations provide no consideration or special privileges. The Service does not deliver a digital good or
            service as part of a donation.</p>

        <h2>9. Third‑party services and map data</h2>
        <p>The Service uses map services from <strong>Apple Maps (iOS)</strong> and <strong>Google Maps
            (Android)</strong>. Use of these services is subject to the providers’ own terms and privacy policies.
            Location data and map displays may be imprecise. Respect private property and local rules when visiting
            listed stands/locations. Sælv is not a party to any transaction between you and a stand/vendor.</p>

        <h2>10. Intellectual property</h2>
        <p>Code, structure, design, databases, logos, and trade names are owned by Sælv or licensors. No rights are
            transferred beyond Section 4. You may not copy or exploit our content (including <strong>scraping/text and
                data mining</strong>) beyond what non‑waivable law permits.</p>

        <h2>11. Feedback</h2>
        <p>We may freely use suggestions/ideas without compensation.</p>

        <h2>12. Data protection</h2>
        <p>See our <a href="https://saelv.dk/privatlivspolitik">Privacy Policy</a> for details on personal data
            processing, including account data (user ID, name, e‑mail, and push tokens). Data are hosted in the EU. You
            can delete your account and data as described in Section 17.</p>

        <h2>13. Security</h2>
        <p>We implement appropriate technical and organisational measures (e.g., HTTPS and access control for private
            content), but complete security cannot be guaranteed. Your use is at your own risk.</p>

        <h2>14. System requirements, compatibility, and updates</h2>
        <ul>
            <li><strong>iOS:</strong> iOS 17 or later.</li>
            <li><strong>Android:</strong> Android 10 (API 29) or later.</li>
            <li><strong>Updates:</strong> We provide functional and security updates for as long as the app is available
                in the respective app stores.
            </li>
        </ul>

        <h2>15. Limitation of liability</h2>
        <p>To the extent permitted by law: (a) We are not liable for indirect or consequential losses, loss of business,
            data loss, or loss of goodwill; (b) Our total liability per 12‑month period is limited to <strong>DKK
                1,000</strong> or the sum of your donations in that period (whichever is higher); (c) Nothing in these
            Terms limits liability for wilful misconduct/gross negligence, death/personal injury caused by negligence,
            product liability, data protection breaches where limitation is not permitted, or any other non‑waivable
            rights.</p>

        <h2>16. Disclaimer of warranties</h2>
        <p>The Service is provided “as is” and “as available”, without express or implied warranties (including
            merchantability, fitness for a particular purpose, non‑infringement, and availability), to the extent
            allowed by law.</p>

        <h2>17. Termination, deletion, and retention</h2>
        <p>You may stop using the Service or <strong>request deletion in‑app</strong> at any time. Deletion takes
            effect <strong>immediately</strong> in active systems. Temporary technical copies in caches/backups are
            overwritten in the ordinary course of operations. Data necessary to comply with legal obligations (e.g.,
            bookkeeping, security, legal claims) may be retained for the applicable statutory periods.</p>
        <p>We may suspend or close accounts for material/repeated breaches of these Terms, for legal requirements, or
            for security risks. Repeated violations may lead to exclusion from the platform.</p>

        <h2>18. Changes to these Terms</h2>
        <p>Material changes will be announced at least <strong>30 days</strong> before taking effect via in‑app notice
            and/or e‑mail (if provided) and, where relevant, push notification. <strong>Continued use after the
                effective date</strong> constitutes acceptance. A version history is available in the respective app
            stores.</p>

        <h2>19. Apple and Google platform terms</h2>
        <p><strong>iOS:</strong> Use of the iOS app is subject to <strong>Apple’s Standard EULA</strong>. Apple and
            Apple’s subsidiaries are third‑party beneficiaries of these Terms and may enforce them against you. Support
            and maintenance are provided by Sælv, not Apple.</p>
        <p><strong>Google Play (Android):</strong> No special terms beyond these Terms. Support and maintenance are
            provided by Sælv.</p>

        <h2>20. Complaints (Denmark)</h2>
        <p>If a dispute cannot be resolved directly, consumers in Denmark may complain to the <strong>Danish Consumer
            Complaints Center (Nævnenes Hus – Center for Klageløsning)</strong>, Toldboden 2, 8800 Viborg, phone +45 72
            40 56 00, or use the <strong>Complaint Portal (Klageportalen)</strong> on Nævnenes Hus’ website. Access
            conditions and thresholds are set by Nævnenes Hus.</p>

        <h2>21. Governing law and venue</h2>
        <p>These Terms are governed by <strong>Danish law</strong> (excluding conflict‑of‑law rules). Disputes may be
            brought before the Danish courts with venue in <strong>Copenhagen</strong>, unless non‑waivable consumer
            protection rules in your country of residence provide otherwise.</p>

        <h2>22. Force majeure</h2>
        <p>We are not liable for circumstances beyond reasonable control (e.g., power outages, network failures,
            governmental orders, cyberattacks, natural events).</p>

        <h2>23. Severability</h2>
        <p>If any provision is invalid or unenforceable, the remaining provisions remain in effect; the invalid
            provision is replaced by a valid one that achieves a similar purpose.</p>

        <h2>24. Language</h2>
        <p>These Terms are available in Danish and English. In case of discrepancies, the <strong>Danish version
            prevails</strong>.</p>

        <h2>25. Contact</h2>
        <p>Questions: <a href="mailto:kontakt@saelv.dk">kontakt@saelv.dk</a></p>
    </div>;
}